import { useCallback, useEffect, useRef, useState } from "react";
import type { ClinicianMeetingCalendar } from "@care-by-ella/contracts";
import { fetchMeetingCalendar, UnauthorizedError } from "@/lib/clinician-api";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";

// TES-1233 — one range of the calendar, polled like the shell's queues so a
// patient joining or a new booking shows without a reload. The previous
// range's data stays on screen while the next one loads, instead of the grid
// blinking empty on every week step.
const POLL_MS = 30_000;

export function useMeetingCalendar(
  from: Date,
  to: Date,
  onUnauthorized: () => void,
): {
  data: ClinicianMeetingCalendar | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<ClinicianMeetingCalendar | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const fromMs = from.getTime();
  const toMs = to.getTime();
  // Ignore an answer that arrives after the range has moved on.
  const latest = useRef(0);

  const reload = useCallback(() => {
    const ticket = ++latest.current;
    setLoading(true);
    fetchMeetingCalendar(new Date(fromMs), new Date(toMs))
      .then((d) => {
        if (ticket !== latest.current) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        if (ticket === latest.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (ticket === latest.current) setLoading(false);
      });
  }, [fromMs, toMs]);

  useEffect(() => {
    reload();
    const id = setInterval(reload, POLL_MS);
    return () => clearInterval(id);
  }, [reload]);
  // TES-1383 — and at once when the doctor comes back to the tab.
  useRefreshOnFocus(reload);

  return { data, error, loading, reload };
}
